import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Music2, VolumeX } from "lucide-react";
import { fetchBook, trackAudioUrl } from "../lib/api";

// Routes where the book's soundtrack should keep playing in the background.
const ACTIVE = ["/vault", "/vault/gallery"];
const TARGET_VOL = 0.45;

export const AmbientMusic = () => {
  const location = useLocation();
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [trackId, setTrackId] = useState(null);
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem("bnm-ambient-pref") !== "off"; } catch (e) { return true; }
  });
  const [playing, setPlaying] = useState(false);
  const isActive = ACTIVE.includes(location.pathname);

  // Pick the track to carry over: whatever last played in the book, else the opener.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const last = sessionStorage.getItem("bnm-last-track");
        if (last) { if (alive) setTrackId(last); return; }
        const book = await fetchBook();
        const t = (book?.tracks && book.tracks[0] && book.tracks[0].id) || null;
        if (alive) setTrackId(t);
      } catch (e) { /* no soundtrack available */ }
    })();
    return () => { alive = false; };
  }, []);

  const fadeTo = (target, onDone) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const step = 0.05;
    fadeRef.current = setInterval(() => {
      const v = a.volume;
      if (Math.abs(v - target) <= step) {
        a.volume = target;
        clearInterval(fadeRef.current);
        fadeRef.current = null;
        if (onDone) onDone();
        return;
      }
      a.volume = Math.min(1, Math.max(0, v + (target > v ? step : -step)));
    }, 60);
  };

  // Start / stop playback as the reader moves between pages.
  useEffect(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.loop = true;
      a.preload = "auto";
      a.volume = 0;
      audioRef.current = a;
    }
    const a = audioRef.current;
    if (!trackId) return;

    const want = isActive && on;
    if (want) {
      const src = trackAudioUrl(trackId);
      if (!a.src || !a.src.includes(encodeURIComponent(trackId)) && a.src !== src) a.src = src;
      const tryPlay = () => a.play().then(() => { setPlaying(true); fadeTo(TARGET_VOL); }).catch(() => false);
      tryPlay().then((ok) => {
        if (ok === false) {
          // Browser blocked autoplay — resume on the reader's next interaction.
          const kick = () => { tryPlay(); cleanup(); };
          const cleanup = () => {
            document.removeEventListener("pointerdown", kick);
            document.removeEventListener("keydown", kick);
          };
          document.addEventListener("pointerdown", kick, { once: true });
          document.addEventListener("keydown", kick, { once: true });
        }
      });
    } else {
      fadeTo(0, () => { try { a.pause(); } catch (e) {} });
      setPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, on, trackId]);

  // Clean up the audio element on unmount.
  useEffect(() => () => {
    if (fadeRef.current) clearInterval(fadeRef.current);
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} }
  }, []);

  const toggle = () => {
    setOn((prev) => {
      const next = !prev;
      try { localStorage.setItem("bnm-ambient-pref", next ? "on" : "off"); } catch (e) {}
      return next;
    });
  };

  if (!isActive || !trackId) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="ambient-music-toggle"
      aria-label={on ? "Mute background music" : "Play background music"}
      className="fixed bottom-5 right-5 z-[60] inline-flex h-11 w-11 items-center justify-center rounded-full backdrop-blur transition-transform hover:scale-105"
      style={{
        background: "rgba(18,21,28,0.72)",
        border: "1px solid rgba(212,163,115,0.3)",
        color: "#D4A373",
        boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
      }}
    >
      {on && playing ? <Music2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
      {on && playing && (
        <span className="pointer-events-none absolute inset-0 rounded-full"
          style={{ border: "1px solid rgba(212,163,115,0.5)", animation: "ambientPulse 2.4s ease-out infinite" }} />
      )}
    </button>
  );
};

export default AmbientMusic;
